// src/app/products/utils.ts

import { supabase } from '@/lib/supabase/client';
import { DisplayVendorProduct, DisplayMenuItem, GroupedProducts, GroupedAdminProducts, GroupedMenuItems, GroupedAdminMenuItems, ProductUpdateData, VendorSubcategory } from './types';
import { Database } from '@/types/supabase';

type VendorCourse = Database['public']['Tables']['vendor_courses']['Row'];

export const fetchVendorProducts = async (userId: string): Promise<DisplayVendorProduct[]> => {
  try {
    // First, get the vendor_id from the profiles table
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('vendor_id')
      .eq('id', userId)
      .single();

    if (profileError || !profileData?.vendor_id) {
      return [];
    }

    // Then fetch products for this vendor
    const { data: products, error: productsError } = await supabase
      .from('vendor_products')
      .select('*')
      .eq('vendor_id', profileData.vendor_id)
      .order('name');

    if (productsError) {
      return [];
    }

    return products as DisplayVendorProduct[];
  } catch {
    return [];
  }
};

export const fetchVendorMenuItems = async (userId: string): Promise<DisplayMenuItem[]> => {
  try {
    // First, get the vendor_id from the profiles table
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('vendor_id')
      .eq('id', userId)
      .single();

    if (profileError || !profileData?.vendor_id) {
      return [];
    }

    const vendorId = profileData.vendor_id;

    // Fetch products
    const { data: products, error: productsError } = await supabase
      .from('vendor_products')
      .select('*')
      .eq('vendor_id', vendorId)
      .order('name');

    if (productsError) {
      console.error('Error fetching products:', productsError);
      return [];
    }

    // Fetch combos with their groups and products
    const { data: combos, error: combosError } = await supabase
      .from('combos')
      .select(`
        *,
        combo_groups (
          id,
          name,
          is_optional,
          display_order,
          group_type,
          combo_group_products (
            id,
            product_id,
            vendor_products:product_id (
              id,
              name,
              description,
              image_url,
              is_vegetarian,
              contains_egg
            )
          )
        )
      `)
      .eq('vendor_id', vendorId)
      .order('name');

    if (combosError) {
      console.error('Error fetching combos:', combosError);
      return [];
    }

    // Convert products to DisplayMenuItem format
    const productMenuItems: DisplayMenuItem[] = (products || []).map(product => ({
      id: product.id,
      name: product.name,
      description: product.description ?? undefined,
      image_url: product.image_url ?? undefined,
      food_course: product.food_course ?? undefined,
      is_vegetarian: product.is_vegetarian ?? false,
      contains_egg: product.contains_egg ?? false,
      not_a_food: product.not_a_food ?? false,
      vendor_id: product.vendor_id ?? undefined,
      type: 'product' as const
    }));

    // Convert combos to DisplayMenuItem format
    const comboMenuItems: DisplayMenuItem[] = (combos || []).map(combo => ({
      id: combo.id,
      name: combo.name,
      description: combo.description ?? undefined,
      image_url: combo.image_url ?? undefined,
      food_course: combo.course_id ?? undefined,
      is_vegetarian: false, // Combos don't have this field directly
      contains_egg: false, // Combos don't have this field directly
      vendor_id: combo.vendor_id ?? undefined,
      type: 'combo' as const,
      combo_groups: (combo.combo_groups ?? []).map(group => ({
        ...group,
        group_type: group.group_type as 'selection' | 'individual',
        combo_group_products: (group.combo_group_products ?? []).map(cgp => ({
          ...cgp,
          vendor_products: cgp.vendor_products ? {
            ...cgp.vendor_products,
            is_vegetarian: cgp.vendor_products.is_vegetarian ?? false,
            contains_egg: cgp.vendor_products.contains_egg ?? false
          } : undefined
        }))
      }))
    }));

    // Combine and sort by name
    const allMenuItems = [...productMenuItems, ...comboMenuItems];
    return allMenuItems.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('Error fetching vendor menu items:', error);
    return [];
  }
};

export const fetchFoodCourses = async (): Promise<string[]> => {
  const { data, error: fetchError } = await supabase
    .from('vendor_products')
    .select('food_course')
    .order('food_course');

  if (fetchError) {
    return [];
  }

  return Array.from(new Set(data.map(item => item.food_course).filter((course): course is string => course !== null)));
};

export const addToVendorMenu = async (
  userId: string,
  productData: Partial<DisplayVendorProduct>
): Promise<DisplayVendorProduct | null> => {
  if (!userId) {
    return null;
  }

  const { data, error } = await supabase
    .from('vendor_products')
    .insert({
      vendor_id: userId,
      name: productData.name || '',
      description: productData.description,
      food_course: productData.food_course || '',
      is_vegetarian: productData.is_vegetarian || false,
      contains_egg: productData.contains_egg || false,
      image_url: productData.image_url || null
    })
    .select()
    .single();

  if (error || !data) {
    return null;
  }

  return data as DisplayVendorProduct;
};

export const removeFromVendorMenu = async (vendorProductId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('vendor_products')
    .delete()
    .eq('id', vendorProductId);

  if (error) {
    return false;
  }

  return true;
};

export const updateVendorProduct = async (
  vendorProductId: string, 
  data: ProductUpdateData
): Promise<boolean> => {
  // If data is empty, return true without making a database call
  if (Object.keys(data).length === 0) {
    return true;
  }
  
  // Update the vendor_products table directly with the provided data
  const { error } = await supabase
    .from('vendor_products')
    .update(data)
    .eq('id', vendorProductId);

  if (error) {
    return false;
  }

  return true;
};

export const groupVendorProductsByCourse = (vendorProducts: DisplayVendorProduct[]): GroupedAdminProducts => {
  const grouped = vendorProducts.reduce((acc, product) => {
    // For basic grouping without course data, we still display UUIDs
    // This function should ideally not be used when courses are available
    const course = product.food_course || 'Not food';
    if (!acc[course]) {
      acc[course] = [];
    }
    acc[course].push(product);
    return acc;
  }, {} as GroupedProducts);

  return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
};

export const groupVendorProductsByCoursePriority = (
  vendorProducts: DisplayVendorProduct[], 
  courses: VendorCourse[]
): GroupedAdminProducts => {
  // Create a mapping of course IDs to course names
  const courseIdToNameMap = courses.reduce((acc, course) => {
    acc[course.id] = course.name;
    return acc;
  }, {} as Record<string, string>);

  // Create a mapping of course names to their priorities
  const coursePriorityMap = courses.reduce((acc, course) => {
    // Explicitly handle priority 0 and null/undefined separately
    acc[course.name] = course.priority ?? null;
    return acc;
  }, {} as Record<string, number | null>);

  // Group products by course name (mapped from UUID)
  const grouped = vendorProducts.reduce((acc, product) => {
    const courseName = product.food_course 
      ? courseIdToNameMap[product.food_course] || 'Unknown Course'
      : 'Not food';
    if (!acc[courseName]) {
      acc[courseName] = [];
    }
    acc[courseName].push(product);
    return acc;
  }, {} as GroupedProducts);

  // Sort by course priority, with fallback to alphabetical for courses not in the priority map
  return Object.entries(grouped).sort(([a], [b]) => {
    const priorityA = coursePriorityMap[a];
    const priorityB = coursePriorityMap[b];
    
    // If both courses have priorities (including 0), sort by priority
    if (priorityA !== null && priorityA !== undefined && priorityB !== null && priorityB !== undefined) {
      return priorityA - priorityB;
    }
    
    // If only one has priority, prioritize it (including priority 0)
    if (priorityA !== null && priorityA !== undefined) return -1;
    if (priorityB !== null && priorityB !== undefined) return 1;
    
    // If neither has priority, sort alphabetically
    return a.localeCompare(b);
  });
};

export async function deleteVendorProduct(
  vendorProductId: string,
  imageUrl: string | null | undefined
) {
  try {
    // First, verify the user is authenticated
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Use the provided imageUrl - never try to fetch it from database to avoid RLS issues
    let imageUrlToDelete = imageUrl;

    // Delete from vendor_products table directly
    const { error: deleteError, count } = await supabase
      .from('vendor_products')
      .delete({ count: 'exact' })
      .eq('id', vendorProductId);

    if (deleteError) {
      console.error('Delete error:', deleteError);
      throw new Error(`Failed to delete product: ${deleteError.message}`);
    }

    // Check if any rows were actually deleted
    if (count === 0) {
      // Product already deleted - this is actually a success case
      return true; 
    }

    // Clean up image from storage if it exists
    if (imageUrlToDelete) {
      try {
        const filePathMatch = imageUrlToDelete.match(/product-images\/(.+)$/);
        if (filePathMatch && filePathMatch[1]) {
          const filePath = filePathMatch[1];
          const { error: storageError } = await supabase
            .storage
            .from('product-images')
            .remove([filePath]);

          if (storageError) {
            console.warn('Failed to delete image from storage:', storageError);
          }
        }
      } catch (error) {
        console.warn('Error cleaning up product image:', error);
      }
    }

    return true;
  } catch (error) {
    console.error('deleteVendorProduct error:', error);
    throw error;
  }
}

// New grouping functions for DisplayMenuItem (products + combos)
export const groupMenuItemsByCourse = (menuItems: DisplayMenuItem[]): GroupedAdminMenuItems => {
  const grouped = menuItems.reduce((acc, item) => {
    const course = item.food_course || 'Not food';
    if (!acc[course]) {
      acc[course] = [];
    }
    acc[course].push(item);
    return acc;
  }, {} as GroupedMenuItems);

  return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
};

export const groupMenuItemsByCoursePriority = (
  menuItems: DisplayMenuItem[], 
  courses: VendorCourse[]
): GroupedAdminMenuItems => {
  // Create a mapping of course IDs to course names
  const courseIdToNameMap = courses.reduce((acc, course) => {
    acc[course.id] = course.name;
    return acc;
  }, {} as Record<string, string>);

  // Create a mapping of course names to priorities
  const coursePriorityMap = courses.reduce((acc, course) => {
    // Explicitly handle priority 0 and null/undefined separately
    acc[course.name] = course.priority ?? null;
    return acc;
  }, {} as Record<string, number | null>);

  const grouped = menuItems.reduce((acc, item) => {
    let courseName: string;
    
    if (item.food_course) {
      // If food_course is a UUID, look up the course name
      if (courseIdToNameMap[item.food_course]) {
        courseName = courseIdToNameMap[item.food_course];
      } else {
        // If food_course is already a name, use it directly
        courseName = item.food_course;
      }
    } else {
      courseName = 'Not food';
    }
    
    if (!acc[courseName]) {
      acc[courseName] = [];
    }
    acc[courseName].push(item);
    return acc;
  }, {} as GroupedMenuItems);

  return Object.entries(grouped).sort(([a], [b]) => {
    const priorityA = coursePriorityMap[a];
    const priorityB = coursePriorityMap[b];
    
    // If both courses have priorities (including 0), sort by priority
    if (priorityA !== null && priorityA !== undefined && priorityB !== null && priorityB !== undefined) {
      return priorityA - priorityB;
    }
    
    // If only one has priority, prioritize it (including priority 0)
    if (priorityA !== null && priorityA !== undefined) return -1;
    if (priorityB !== null && priorityB !== undefined) return 1;
    
    // If neither has priority, sort alphabetically
    return a.localeCompare(b);
  });
};

// New hierarchical grouping function for two-level category/subcategory structure
export const groupMenuItemsHierarchically = (
  menuItems: DisplayMenuItem[], 
  courses: VendorCourse[],
  subcategories: VendorSubcategory[]
): [string, { items: DisplayMenuItem[]; subcategories: [string, DisplayMenuItem[]][] }][] => {
  // Create mappings for efficiency
  const courseIdToNameMap = courses.reduce((acc, course) => {
    acc[course.id] = course.name;
    return acc;
  }, {} as Record<string, string>);

  const coursePriorityMap = courses.reduce((acc, course) => {
    // Explicitly handle priority 0 and null/undefined separately
    acc[course.name] = course.priority ?? null;
    return acc;
  }, {} as Record<string, number | null>);

  const subcategoryMap = subcategories.reduce((acc, sub) => {
    acc[sub.id] = sub;
    return acc;
  }, {} as Record<string, VendorSubcategory>);

  // Group items by course first
  const courseGroups = menuItems.reduce((acc, item) => {
    let courseName: string;
    
    if (item.food_course) {
      courseName = courseIdToNameMap[item.food_course] || item.food_course;
    } else {
      courseName = 'Not food';
    }
    
    if (!acc[courseName]) {
      acc[courseName] = [];
    }
    acc[courseName].push(item);
    return acc;
  }, {} as Record<string, DisplayMenuItem[]>);

  // Now process each course to separate items with/without subcategories
  const hierarchicalGroups = Object.entries(courseGroups).map(([courseName, courseItems]) => {
    const itemsWithoutSubcategory: DisplayMenuItem[] = [];
    const subcategoryGroups: Record<string, DisplayMenuItem[]> = {};

    // Separate items by subcategory
    courseItems.forEach(item => {
      if (item.subcategory_id && subcategoryMap[item.subcategory_id]) {
        const subcategory = subcategoryMap[item.subcategory_id];
        if (!subcategoryGroups[subcategory.name]) {
          subcategoryGroups[subcategory.name] = [];
        }
        subcategoryGroups[subcategory.name].push(item);
      } else {
        itemsWithoutSubcategory.push(item);
      }
    });

    // Sort subcategories by priority
    const sortedSubcategories = Object.entries(subcategoryGroups)
      .map(([name, items]) => {
        const subcategory = subcategories.find(sub => sub.name === name);
        return { name, items, priority: subcategory?.priority || 999 };
      })
      .sort((a, b) => a.priority - b.priority)
      .map(({ name, items }) => [name, items] as [string, DisplayMenuItem[]]);

    return [courseName, {
      items: itemsWithoutSubcategory,
      subcategories: sortedSubcategories
    }] as [string, { items: DisplayMenuItem[]; subcategories: [string, DisplayMenuItem[]][] }];
  });

  // Sort courses by priority
  return hierarchicalGroups.sort(([a], [b]) => {
    const priorityA = coursePriorityMap[a];
    const priorityB = coursePriorityMap[b];
    
    // If both courses have priorities (including 0), sort by priority
    if (priorityA !== null && priorityA !== undefined && priorityB !== null && priorityB !== undefined) {
      return priorityA - priorityB;
    }
    
    // If only one has priority, prioritize it (including priority 0)
    if (priorityA !== null && priorityA !== undefined) return -1;
    if (priorityB !== null && priorityB !== undefined) return 1;
    
    return a.localeCompare(b);
  });
};